// client/src/Stage.jsx

/**
 * This file displays individual round workflows, coordinates raw metric text scales, 
 * renders advisor panels, and prints performance confirmation blocks.
 */

import {
  usePlayer,
  usePlayers,
  useRound,
  useStage,
  useGame,
} from "@empirica/core/player/classic/react";
import { Loading } from "@empirica/core/player/react";
import React from "react";
import VariableIntroduction from "./intro-exit/VariableIntroduction";
import VariableQuiz from "./intro-exit/VariableQuiz";
import FeaturePlots from "./FeaturePlots";
import { PREDICTION, phraseForPercentile } from "./cases.js";
import { variable_theme } from "./theme.js";

// Re-exported for modules that already import it from here.
export { variable_theme };

// ==========================================
// 1. Profile Avatar Sub-Component and Color Theme
// ==========================================
function PanelistAvatar({ iconType, colorClass }) {
  const getEmoji = (type) => {
    switch (type) {
      case "falcon":  return "🦅";
      case "owl":     return "🦉";
      case "badger":  return "🦡";
      case "bear":    return "🐻";
      case "dolphin": return "🐬";
      case "fox":     return "🦊";
      case "raven":   return "🐦";
      default:        return type || "👤"; 
    }
  };

  return (
    <div className={`w-15 h-15 rounded-full border bg-white flex items-center justify-center text-3xl leading-none shadow-xs shrink-0 select-none ${colorClass || "border-gray-300"}`}>
      {getEmoji(iconType)}
    </div>
    
  );
}


// ==========================================
// 2. Main Stage Runtime Handler Component
// ==========================================
export function Stage() {
  const player = usePlayer();
  const players = usePlayers();
  const round = useRound();
  const stage = useStage();
  const game = useGame();

  const [validationError, setValidationError] = React.useState("");
  const [showFeaturePlots, setShowFeaturePlots] = React.useState(false);
  // Inside the quiz stage, Back re-shows the instruction page so an answer can be
  // looked up. It cannot return to the instruction STAGE, which has already closed.
  const [quizShowingInstructions, setQuizShowingInstructions] = React.useState(false);

  if (!player || !round || !stage) {
    return <div className="app-status text-center p-8 text-gray-500 font-sans">Establishing framework channels...</div>;
  }

  const stageName = stage.get("name"); 
  const activeTask = round.get("task");
  const roundNumber = round.get("roundNumber");

  // Fetch treatment parameters dynamically (with safe operational defaults)
  const initialPhaseRounds = game?.get("treatment")?.initialPhaseRounds !== undefined
    ? parseInt(game.get("treatment").initialPhaseRounds, 10)
    : 4;

  // Defaults must match the server's (callbacks.js), or the client computes a
  // different totalGameRounds than the server's lastRoundNumber and the end of the
  // game is never detected: no "Finish Experiment" button and no exitStatus.
  const postRewireEasyRounds = game?.get("treatment")?.postRewireEasyRounds !== undefined
    ? parseInt(game.get("treatment").postRewireEasyRounds, 10)
    : 1;

  const postRewireHardRounds = game?.get("treatment")?.postRewireHardRounds !== undefined
    ? parseInt(game.get("treatment").postRewireHardRounds, 10)
    : 1;

  const totalGameRounds = initialPhaseRounds + postRewireEasyRounds + postRewireHardRounds;

  const bonusFactor = game?.get("treatment")?.correctPredictionBonus !== undefined
    ? parseFloat(game.get("treatment").correctPredictionBonus)
    : 0.50;

  // Render static loader states when choices are locked down
  if (player.stage?.get("submit")) {
    if (players.length === 1) return <Loading />;
    
    const isSetupStage = activeTask === "variable_introduction" || 
                         stageName === "viewVariablesAndSamples" || 
                         stageName === "View Variables And Samples" ||
                         stageName === "variableQuiz";

    const isRewireStage = activeTask === "network_rewire";
    const isFeedbackStage = stageName === "feedback";

    let loadingText = "Prediction locked. Waiting for advisors...";
    
    if (isSetupStage) {
      loadingText = "Waiting for advisors...";
    } else if (isRewireStage) {
      loadingText = "New advisors chosen. Waiting for other players...";
    } else if (isFeedbackStage) {
      loadingText = "Waiting for other players to start next round...";
    }

    return (
      <div className="app-status text-center p-8 text-gray-400 font-sans">
        {loadingText}
      </div>
    );
  }
  // Render the comprehension question that follows the variable introduction.
  if (stageName === "variableQuiz") {
    return (
      <div className="max-w-7xl w-full mx-auto my-2 bg-white p-5 rounded-xl shadow-md border border-gray-100 font-sans space-y-5">
        {quizShowingInstructions ? (
          <>
            <VariableIntroduction player={player} />
            <div className="p-4 pt-1">
              <button
                type="button"
                onClick={() => setQuizShowingInstructions(false)}
                className="app-btn w-full bg-gray-900 text-white py-3 rounded-lg hover:bg-gray-800 transition shadow-md"
              >
                Back to the Question
              </button>
            </div>
          </>
        ) : (
          <VariableQuiz
            player={player}
            onBack={() => setQuizShowingInstructions(true)}
            onPass={() => player.stage.set("submit", true)}
          />
        )}
      </div>
    );
  }

  // Render introductory documentation screens
  if (stageName === "viewVariablesAndSamples" || stageName === "View Variables And Samples" || activeTask === "variable_introduction") {
    return (
      <div className="max-w-7xl w-full mx-auto my-2 bg-white p-5 rounded-xl shadow-md border border-gray-100 font-sans space-y-5">
        <VariableIntroduction player={player} />

        <div className="p-4 pt-1">
          <button
            type="button"
            onClick={() => player.stage.set("submit", true)}
            className="app-btn w-full bg-gray-900 text-white py-3 rounded-lg hover:bg-gray-800 transition shadow-md"
          >
            I Understand These Variables — Continue
          </button>
        </div>
      </div>
    );
  }

  // Task routing infrastructure logic
  switch (activeTask) {

    // ==========================================
    // 3. Loan Default Assessment Task View
    // ==========================================
    case "loan_default_prediction": {
      if (roundNumber === undefined) {
        return <div className="app-status text-center p-8 text-gray-500 font-sans">Syncing server variables...</div>;
      }

      // Render the dedicated transition screen when stageName is "realGameTransition"
      if (stageName === "realGameTransition") {
        return (
          <div className="max-w-xl w-full mx-auto my-12 bg-white p-8 rounded-2xl shadow-lg border border-amber-200 font-sans space-y-6 text-center">
            <div className="app-badge inline-block bg-amber-50 border border-amber-200 text-amber-800 px-4 py-1.5 rounded-full">
              Practice Round Completed
            </div>

            <h2 className="app-hero-title text-gray-900">
              The Real Game Starts Now
            </h2>

            <p className="app-body text-gray-600 max-w-md mx-auto">
              Please pay close attention. Your performance in the upcoming rounds <strong className="text-gray-900 font-semibold">will directly affect your financial compensation</strong>.
            </p>

            <div className="app-section-subtitle pt-2 border-t border-gray-100 flex items-center justify-center gap-2 text-amber-700/80 animate-pulse">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Real rounds begin in 10 seconds...</span>
            </div>
          </div>
        );
      }

      const hasAI = player.get("hasAI");
      const isExpert = player.get("isExpert");

      const tPrivate = variable_theme.private;
      const tAI = variable_theme.ai;
      const tPublic = variable_theme.public;

    const pubSignal1 = round.get("pubSignal1");
    if (pubSignal1 === undefined) {
      return <div className="app-status text-center p-8 text-gray-500">Loading round variables...</div>;
}
    const pubSignal2 = round.get("pubSignal2");
    const aiScore = round.get("aiScore");
    const trueOutcome = round.get("trueOutcome");

    const displayPrivateSignal = isExpert 
        ? (round.get("strongSignal") !== undefined ? `${round.get("strongSignal")}` : "0%") 
        : (round.get("weakSignal") !== undefined ? `${round.get("weakSignal")}%` : "0%");
      
      const privateVariableName = isExpert ? "Credit Score" : "Revolving Utilization";

      // Under each raw value, where that value sits among past borrowers.
      //
      // This is deliberately a PERCENTILE and not a probability of default. An
      // earlier version printed the probability, and it left nothing for the AI to
      // add -- converting raw features into a probability is precisely the AI's job
      // in this design. A percentile does the smaller thing that was actually
      // missing: it tells a participant that a credit score of 463 is low, without
      // telling them what to predict. The exit survey says experts know the credit
      // score is theirs to use; their hard-case accuracy says they cannot turn a
      // value into a verdict, and a value with no reference point is why.
      //
      // Shown for all three raw features rather than only the private one, so the
      // display does not itself mark one card as the one to use. The AI card is
      // excluded: its number is already a probability, not a raw measurement.
      //
      // Each line reads "worse than X% of other loans", which is the same number in
      // the same direction on every card: bigger is always more dangerous, whether
      // worse means a higher value (debt-to-income, revolving utilization) or a
      // lower one (credit score, credit limit). See phraseForPercentile in cases.js.
      const pctLine = (key, value) =>
        value === undefined || value === null || value === ""
          ? null
          : phraseForPercentile(key, value);

      const pubSignal1Pct = pctLine("dti", round.get("pubSignal1Pct"));
      const pubSignal2Pct = pctLine("total_rev_hi_lim", round.get("pubSignal2Pct"));
      const privatePct = pctLine(isExpert ? "fico" : "revol_util",
                                 round.get(isExpert ? "strongSignalPct" : "weakSignalPct"));
      const initialSaved = player.round.get("initialGuess");
      const finalSaved = player.round.get("finalGuess");

      // NOTE: do not write finalGuess here. This runs during render, on every
      // re-render and on every stage, so it used to seed finalGuess before the
      // player had chosen anything in the second-prediction stage. Around a
      // round boundary initialSaved can still hold the PREVIOUS round's value
      // while player.round has already advanced, so the new round's finalGuess
      // got seeded with a stale guess -- which then won over initialSaved below
      // and lit up the wrong button on the second prediction.
      //
      // finalGuess is still guaranteed to be recorded without this: the submit
      // handler below writes it if the player never changed their pick, and the
      // server's onStageEnded backstop covers a stage timeout.
      const activePrediction = stageName === "finalPrediction"
        ? (finalSaved !== undefined ? finalSaved : initialSaved)
        : initialSaved;

      const handleSelection = (val) => {
        setValidationError(""); 
        if (stageName === "initialPrediction") player.round.set("initialGuess", val);
        if (stageName === "finalPrediction") player.round.set("finalGuess", val);
      };

      const getSubmitButtonText = () => {
      // 1. Final end of game
      if (roundNumber === totalGameRounds && stageName === "feedback") {
        return "Finish Experiment";
      }

      // 2. Logic to detect transition to Advisor selection
      // This assumes the rewire happens after the initial phase rounds
      if (stageName === "feedback" && roundNumber === initialPhaseRounds) {
        return "Proceed to Choosing Advisors";
      }

      // 3. Standard flow
      switch (stageName) {
        case "initialPrediction": 
          return "Submit First Prediction & Unlock Advisor Predictions";
        case "finalPrediction": 
          return "Submit Second Prediction";
        case "feedback":
          return roundNumber === 0 
            ? "Complete Practice Round & Begin Real Experiment" 
            : `Proceed to Round ${roundNumber + 1}`;
        default: 
          return "Submit & Next";
      }
    };

      return (        
        <div className="max-w-7xl w-full mx-auto my-1 space-y-3">
          {roundNumber === 0 && (
            <div className="bg-amber-100 border-2 border-amber-400 px-6 py-3.5 rounded-2xl text-center shadow-sm w-full animate-pulse">
              <strong className="app-banner-title block text-amber-900">
                Practice Round Active
              </strong>
              <span className="app-banner-text text-amber-800">
                Performance will not affect real compensation scores
              </span>
            </div>
          )}

          <div className="w-full bg-white p-6 rounded-xl shadow-md border border-gray-100 font-sans space-y-5">{/* RAW METRICS ON TOP (3 COLS), AI ON ROW 2 (CENTERED) */}
            {/* ROW 1: THE THREE RAW BORROWER VARIABLES */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Signal 1: Debt-to-Income */}
              <div className={`p-3 rounded-xl border ${tPublic.borderColor} ${tPublic.bgColor} text-center flex flex-col justify-center min-h-[76px]`}>
                <span className={`app-card-label ${tPublic.textColor} block mb-1`}>Debt-to-Income</span>
                <strong className={`app-card-value ${tPublic.textColor}`}>{pubSignal1.toFixed(1)}%</strong>
                {pubSignal1Pct && <span className="app-card-note block mt-1">{pubSignal1Pct}</span>}
              </div>

              {/* Signal 2: Total Revolving Credit Limit */}
              <div className={`p-3 rounded-xl border ${tPublic.borderColor} ${tPublic.bgColor} text-center flex flex-col justify-center min-h-[76px]`}>
                <span className={`app-card-label ${tPublic.textColor} block mb-1`}>
                  Total Revolving Credit Limit
                </span>
                <strong className={`app-card-value ${tPublic.textColor}`}>${pubSignal2?.toLocaleString()}</strong>
                {pubSignal2Pct && <span className="app-card-note block mt-1">{pubSignal2Pct}</span>}
              </div>

              {/* Signal 3: Private Info (With clean forced line break) */}
              <div className={`p-3 rounded-xl border ${tPrivate.borderColor} ${tPrivate.bgColor} text-center flex flex-col justify-center min-h-[76px]`}>
                <span className={`app-card-label ${tPrivate.textColor} block mb-1`}>{privateVariableName}
                  {/* <span className="block text-[10px] font-semibold normal-case mt-0.5">(Private Information)</span> */}
                  </span>
                <strong className={`app-card-value ${tPrivate.textColor}`}>{displayPrivateSignal}</strong>
                {privatePct && <span className="app-card-note block mt-1">{privatePct}</span>}
              </div>
            </div>

            {/* ROW 2: AI RECOMMENDATION (Only renders if treating with AI, centered and matching size) */}
            {hasAI && (
              <div className="flex justify-center">
                <div className={`w-full md:w-1/3 p-3 rounded-xl border ${tAI.borderColor} ${tAI.bgColor} text-center flex flex-col justify-center min-h-[76px]`}><span className={`app-card-label ${tAI.textColor} block mb-1`}>AI Recommendation</span>
                  <strong className={`app-card-value ${tAI.textColor}`}>{aiScore !== undefined ? `${parseFloat(aiScore).toFixed(1)}%` : "N/A"} Default</strong>
                </div>
              </div>
            )}

            {/* ROW 2b: OPTIONAL FEATURE-RELATIONSHIP PLOTS
                Collapsed by default so the prediction screen still fits a laptop
                viewport; only offered on the two stages where a prediction is
                actually being made. */}
            {(stageName === "initialPrediction" || stageName === "finalPrediction") && (
              <div className="border-t pt-2">
                <button
                  type="button"
                  onClick={() => setShowFeaturePlots((shown) => !shown)}
                  aria-expanded={showFeaturePlots}
                  className="app-btn-sm mx-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                >
                  <span>{showFeaturePlots ? "Hide" : "Show"} feature relationships</span>
                  <span aria-hidden="true">{showFeaturePlots ? "▲" : "▼"}</span>
                </button>

                {showFeaturePlots && (
                  <FeaturePlots isExpert={isExpert} hasAI={hasAI} />
                )}
              </div>
            )}

          {/* ROW 3: PEER PANEL VISUALIZATION & OUTCOME CALIBRATION */}
          {stageName === "finalPrediction" && players.length > 1 && (
            <div className="bg-purple-50/20 border border-purple-100 p-4 rounded-xl">
              <h3 className="app-micro-label text-purple-600 mb-3 text-center">
                Advisor Predictions
              </h3>
              
              <div className="flex flex-wrap items-stretch justify-center gap-4 w-full">
                {(() => {
                  // 1. Filter the peer pool dynamically based on treatment parameters
                  const connectedPeers = players.filter((p) => {
                    if (p.id === player.id) return false;
                    
                    if (roundNumber <= initialPhaseRounds) {
                      // Match on IDs, not display names. initialAdvisorIds holds the
                      // same set as initialAdvisors, so analysis can join
                      // advisor -> playerRound.initialGuess by id for every round,
                      // before and after the rewire, with no name lookup in between.
                      const assigned = player.get("initialAdvisorIds") || [];
                      return assigned.includes(p.id);
                    }
                    const activeNetwork = stageName === "network_rewire" 
                      ? (player.get("advisorDraftIds") || []) 
                      : (player.get("chosenAdvisorIds") || player.get("initialAdvisorIds") || []);

                    return activeNetwork.includes(p.id);
                  });
                  
                  // 2. Render the active cards
                  return connectedPeers.map((peer) => {
                    const peerGuess = peer.round?.get("initialGuess");
                    return (
                      <div key={peer.id} className="flex justify-between items-center bg-white border border-purple-100 p-4 rounded-xl shadow-2xs min-w-[340px] max-w-md justify-self-center gap-6">
                        <div className="flex items-center gap-3.5">
                          <PanelistAvatar iconType={peer.get("_panelIconType")} colorClass={peer.get("_panelColorClass")} />
                          <span className={`app-badge px-2.5 py-1 rounded-md border whitespace-nowrap ${peer.get("panelColorClass")}`}>
                            {peer.get("anonymousName")}
                          </span>
                        </div>
                        <span className={`app-badge shrink-0 ${peerGuess === undefined ? "text-gray-400" : peerGuess === PREDICTION.DEFAULT ? "text-red-600" : "text-green-600"}`}>
                          {peerGuess === undefined ? "No Prediction" : peerGuess === PREDICTION.DEFAULT ? "[Predicts Default]" : "[Predicts Repaid]"}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* STAGE 3 DISPLAY (RECONSTRUCTED PERFORMANCE EVALUATION MATRIX TABLE)*/}
          {stageName === "feedback" && (
            <div className="space-y-4 max-w-3xl mx-auto w-full">
              <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm bg-white">
                <table className="app-table-cell min-w-full text-left font-sans border-collapse">
                  <thead>
                    <tr className="app-table-header bg-gray-100 text-gray-700 border-b border-gray-200">
                      <th className="px-4 py-2.5">Stage</th>
                      <th className="px-4 py-2.5 text-center">Your Prediction</th>
                      <th className="px-4 py-2.5 text-center">Actual Outcome</th>
                      <th className="px-4 py-2.5 text-center">Round Outcome</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 font-medium text-gray-700">
                    
                    {/* Row 1: Initial Prediction Metrics */}
                    <tr className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-500">First Prediction</td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-900">
                        {initialSaved === undefined 
                          ? <span className="text-gray-500 italic">No Prediction</span> 
                          : initialSaved}
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-900">
                        {trueOutcome === PREDICTION.DEFAULT ? "Defaulted" : "Repaid"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {initialSaved === undefined ? (
                          <span className="text-gray-900 italic">N/A</span>
                        ) : (
                          <span className={`app-badge px-2 py-0.5 rounded inline-block font-mono border ${
                            initialSaved === trueOutcome 
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700" 
                              : "bg-rose-50 border-rose-200 text-rose-700"
                          }`}>
                            {initialSaved === trueOutcome ? `Correct (+$${bonusFactor.toFixed(2)})` : "Incorrect"}
                          </span>
                        )}
                      </td>
                    </tr>

                    {/* Row 2: Final Prediction Metrics */}
                    <tr className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-500">Second Prediction</td>
                      <td className="px-4 py-3 text-center text-gray-900 font-semibold">
                        {finalSaved === undefined 
                          ? <span className="text-gray-500 italic">No Prediction</span> 
                          : finalSaved}
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-900">
                        {trueOutcome === PREDICTION.DEFAULT ? "Defaulted" : "Repaid"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {finalSaved === undefined ? (
                          <span className="text-gray-900 italic">N/A</span>
                        ) : (
                          <span className={`app-badge px-2 py-0.5 rounded inline-block font-mono border ${
                            finalSaved === trueOutcome 
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700" 
                              : "bg-rose-50 border-rose-200 text-rose-700"
                          }`}>
                            {finalSaved === trueOutcome ? `Correct (+$${bonusFactor.toFixed(2)})` : "Incorrect"}
                          </span>
                        )}
                      </td>
                    </tr>

                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ROW 4: INTERACTIVE SELECTION PROMPTS AND CONTROL BUTTONS */}
          {(stageName === "initialPrediction" || stageName === "finalPrediction") && (
            <div className="mt-2 border-t pt-4">
              <label className="app-micro-label block text-gray-400 mb-2 text-center">
                Select your {stageName === "initialPrediction" ? "First" : "Second"} Prediction:
              </label>
              <div className="flex gap-4 max-w-xl mx-auto">
                <button
                  type="button"
                  onClick={() => handleSelection(PREDICTION.REPAID)}
                  className={`app-btn flex-1 py-2.5 rounded-lg border transition ${activePrediction === PREDICTION.REPAID ? "bg-green-600 text-white border-green-600 shadow-sm" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
                >
                  Predict Repaid
                </button>
                <button
                  type="button"
                  onClick={() => handleSelection(PREDICTION.DEFAULT)}
                  className={`app-btn flex-1 py-2.5 rounded-lg border transition ${activePrediction === PREDICTION.DEFAULT ? "bg-red-600 text-white border-red-600 shadow-sm" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
                >
                  Predict Default
                </button>
              </div>
            </div>
          )}

          {validationError && (
            <div className="app-error bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2 rounded-lg text-center max-w-xl mx-auto animate-pulse">
              {validationError}
            </div>
          )}

          <div className="pt-2">
            <button
              type="button"
              onClick={() => {
                if (activePrediction === undefined && stageName !== "feedback") {
                  setValidationError("Selection mandatory before submission.");
                  return;
                }

                // If moving to submit on final prediction, ensure finalGuess is explicitly saved
                if (stageName === "finalPrediction" && player.round.get("finalGuess") === undefined) {
                  player.round.set("finalGuess", initialSaved ?? PREDICTION.REPAID);
                }

                player.stage.set("submit", true);

                if (roundNumber === totalGameRounds && stageName === "feedback") {
                  player.set("exitStatus", "finished");

                  // Go straight to the exit survey instead of waiting for the
                  // rest of the group. The final feedback stage is the last thing
                  // that needs the group in sync, and by this point this player
                  // has already submitted it, so there is nothing left to
                  // coordinate on.
                  //
                  // player.exit() sets the "ended" attribute, which EmpiricaContext
                  // checks before any game/stage routing -- so this player is
                  // handed to exitSteps immediately. It does not strand anyone
                  // else: Empirica's "have all players submitted" test counts an
                  // ended player as done, so the stage still closes normally for
                  // the players still finishing.
                  player.exit("completed final round");
                }
              }}
              className="app-btn w-full bg-gray-900 text-white py-3 rounded-lg hover:bg-gray-800 transition shadow-xs"
            >
              {getSubmitButtonText()}
            </button> 
          </div>
        </div>  
      </div>
      );
    }
    
    // ==========================================
    // 4. Network Rewiring Choice Section View
    // ==========================================
    case "network_rewire": {
      const roundNumber = round.get("roundNumber");
      const peers = players.filter((p) => p.id !== player.id);
      const activeAdvisors = player.get("advisorDraftIds") || [];
      const requiredAdvisors = players.length <= 4 ? 1 : 2;

      // Define the two pools based on initial setup
      const initialAdvisorIDs = player.get("initialAdvisorIds") || [];
      const discoveryPeerIDs = player.get("alternativeAdvisorIds") || [];
      
      const pastAdvisors = peers.filter(p => initialAdvisorIDs.includes(p.id));
      // No slice here: the server now stores exactly the alternatives that should
      // be offered, so alternativeAdvisorIds and what is rendered stay identical.
      const newPool = peers.filter(p => discoveryPeerIDs.includes(p.id));

      const handleToggle = (peerId) => {
        if (activeAdvisors.includes(peerId)) {
          player.set("advisorDraftIds", activeAdvisors.filter((id) => id !== peerId));
        } else {
          if (activeAdvisors.length >= requiredAdvisors) return; 
          player.set("advisorDraftIds", [...activeAdvisors, peerId]);
        }
      };

      const isValidSelection = activeAdvisors.length === requiredAdvisors;

      return (
        <div className="max-w-4xl w-full bg-white p-5 rounded-xl shadow-md border border-gray-100 font-sans mx-auto my-2 space-y-3">
          <div className="border-b pb-3 text-center">
            <h2 className="app-section-title text-gray-800">Choosing Your Advisors</h2>
            <p className="app-section-subtitle text-gray-600 mt-0.5 max-w-lg mx-auto">
              Review how many First Predictions each advisor got correct in the past {initialPhaseRounds} rounds. Select <strong className="text-gray-700">exactly {requiredAdvisors} member(s)</strong> to act as your advisors for the remaining rounds.
            </p>
          </div>

      {/* SECTION 1: PAST ADVISORS */}
      <div className="space-y-2">
        <h3 className="app-group-heading text-gray-900 mb-2">
          Your Current Advisors
        </h3>
        {pastAdvisors.map((peer) => (
          <PeerRow key={peer.id} peer={peer} isSelected={activeAdvisors.includes(peer.id)} onToggle={handleToggle} />
        ))}
      </div>

      {/* SECTION 2: NEW OPTIONS */}
      <div className="space-y-2 mt-6">
        <h3 className="app-group-heading text-gray-900 mb-2">
          Other Potential Advisors
        </h3>
        {newPool.map((peer) => (
          <PeerRow key={peer.id} peer={peer} isSelected={activeAdvisors.includes(peer.id)} onToggle={handleToggle} />
        ))}
      </div>

          <button
              type="button"
              disabled={!isValidSelection} // This keeps it visually "blurred"/unclickable
              onClick={() => {
                player.set("chosenAdvisorIds", activeAdvisors);
                player.set("network_rewired", 1); // User explicitly confirmed
                player.stage.set("submit", true);
              }}
              className={`app-btn w-full py-2.5 rounded-lg transition text-white ${
                isValidSelection ? "bg-gray-900 hover:bg-gray-800" : "bg-gray-300 cursor-not-allowed opacity-50"
              }`}
            >
              Confirm Advisor Selection & Proceed to Next Round
            </button>
          </div>
      );
    }

    // Simple Helper Component for the row styling
    function PeerRow({ peer, isSelected, onToggle }) {
      return (
        <div className={`flex items-center justify-between p-3 rounded-lg border transition-all ${isSelected ? "bg-blue-50 border-blue-300" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center gap-3">
            <PanelistAvatar iconType={peer.get("_panelIconType")} colorClass={peer.get("_panelColorClass")} />
            <div>
              <h4 className="app-advisor-row font-semibold text-gray-800">{peer.get("anonymousName")}</h4>
              {/* Raw counts rather than a percentage: "2 out of 4" is easier to
                  judge than "50%", and it makes the sample size explicit.
                  countTotalPredictionRounds is the number of scored First
                  Predictions so far, which at this point is the initial phase. */}
              <p className="app-advisor-row text-gray-900">
                {peer.get("countCorrectInitial") ?? 0} correct out of{" "}
                {peer.get("countTotalPredictionRounds") ?? 0} predictions
              </p>
            </div>
          </div>
          <button type="button" onClick={() => onToggle(peer.id)} className={`app-btn-sm px-3 py-1 rounded-md border ${isSelected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300"}`}>
            {isSelected ? "Selected" : "Select"}
          </button>
        </div>
      );
    }
    default:
      return <div className="app-status text-gray-500 p-8 text-center font-sans">Initializing task architecture...</div>;
  }
}
